import {Button, Group, Input} from '@mantine/core';
import type {KeyboardEvent} from 'react';
import {useCallback} from 'react';
import {STATE_COLOR_SHADES} from 'src/design/tokens/colors';
import type {StateColor} from 'src/design/tokens/colors';

/**
 * Single-select group of large touch-friendly buttons, with radiogroup
 * semantics and roving-tabindex arrow-key navigation.
 * @registry Tablet-friendly single-select button group (radiogroup a11y),
 * neutral-filled selection with optional semantic color per option.
 *
 * Design-tier ports lose Mantine/MUI implicit a11y roles, so `role="radiogroup"`
 * / `role="radio"` + `aria-checked` are set explicitly here, matching the
 * lesson from other design-atom overlay work.
 */
export type OptionButtonGroupColor = StateColor;

export interface OptionButtonGroupOption {
  value: string;
  label: string;
  color?: OptionButtonGroupColor;
  icon?: React.ReactNode;
}

export interface OptionButtonGroupProps {
  options: OptionButtonGroupOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  allowDeselect?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: React.ReactNode;
  label?: React.ReactNode;
  /** Accessible name for the radiogroup when `label` is not a plain string. */
  ariaLabel?: string;
  required?: boolean;
  description?: string;
  /** `lg` gives the ~44px targets day-of evaluation flows need. */
  size?: 'sm' | 'lg';
}

const MIN_HEIGHT_BY_SIZE: Record<'sm' | 'lg', number> = {sm: 36, lg: 44};

export const OptionButtonGroup = ({
  options,
  value,
  onChange,
  allowDeselect = true,
  disabled,
  readOnly,
  error,
  label,
  ariaLabel,
  required,
  description,
  size = 'sm',
}: OptionButtonGroupProps) => {
  const select = useCallback(
    (option: OptionButtonGroupOption) => {
      if (disabled || readOnly) return;
      if (option.value === value) {
        if (allowDeselect) onChange(null);
        return;
      }
      onChange(option.value);
    },
    [disabled, readOnly, value, allowDeselect, onChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (disabled || readOnly || options.length === 0) return;

      const previousKeys = ['ArrowLeft', 'ArrowUp'];
      const nextKeys = ['ArrowRight', 'ArrowDown'];
      if (![...previousKeys, ...nextKeys].includes(event.key)) return;

      event.preventDefault();

      const currentIndex = options.findIndex(opt => opt.value === value);
      const delta = previousKeys.includes(event.key) ? -1 : 1;
      const nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + delta + options.length) % options.length;

      const nextOption = options[nextIndex];
      onChange(nextOption.value);

      requestAnimationFrame(() => {
        const container = event.currentTarget;
        const buttons = container?.querySelectorAll<HTMLButtonElement>(
          '[role="radio"]'
        );
        buttons?.[nextIndex]?.focus();
      });
    },
    [disabled, readOnly, options, value, onChange]
  );

  const isWrapped = Boolean(label || description || error);

  const content = (
    <Group
      role="radiogroup"
      aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
      aria-required={required}
      aria-disabled={disabled}
      aria-readonly={readOnly}
      onKeyDown={handleKeyDown}
      gap="xs"
      wrap="wrap"
      mt={isWrapped ? 'xs' : undefined}>
      {options.map(option => {
        const selected = option.value === value;
        const colorProps = selected
          ? option.color
            ? {color: STATE_COLOR_SHADES[option.color], variant: 'filled' as const}
            : {variant: 'filled' as const}
          : {variant: 'default' as const};

        return (
          <Button
            key={option.value}
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (value === null && option === options[0]) ? 0 : -1}
            disabled={disabled}
            size={size}
            mih={MIN_HEIGHT_BY_SIZE[size]}
            title={option.label}
            leftSection={option.icon}
            // flexShrink overrides the theme's global Button flexShrink:0 so
            // options reflow onto extra rows instead of clipping their labels.
            style={{
              flex: '1 1 6rem',
              minWidth: 0,
              flexShrink: 1,
              height: 'auto',
              paddingBlock: 6,
            }}
            styles={{
              label: {
                // Block display drops the label's default height:100% flex
                // centering — height:auto lets the button re-center it.
                display: 'block',
                height: 'auto',
                whiteSpace: 'normal',
                overflow: 'visible',
                textOverflow: 'clip',
              },
            }}
            onClick={() => select(option)}
            {...colorProps}>
            {option.label}
          </Button>
        );
      })}
    </Group>
  );

  if (!label && !description && !error) {
    return content;
  }

  return (
    <Input.Wrapper
      label={label}
      description={description}
      required={required}
      error={error}>
      {content}
    </Input.Wrapper>
  );
};
